// Loads whitelabel theme fonts into the dashboard document so editor previews
// render with the real typefaces. Mirrors the storefront's loader: a theme's
// fontUrl stylesheet (Google Fonts link or self-hosted @font-face css) supplies
// the families itself; otherwise each named family is fetched from Google Fonts.
const loaded = new Set()

export function loadFontStylesheet(href) {
  if (!href || loaded.has(href)) return
  loaded.add(href)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

export function loadGoogleFont(family) {
  if (!family) return
  loadFontStylesheet(
    `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;700;800&display=swap`
  )
}

// Publisher-provided font files: one injected @font-face rule per font.
export function injectCustomFonts(customFonts) {
  for (const font of customFonts || []) {
    if (!font || !font.name || !font.url) continue
    const key = `custom:${font.url}`
    if (loaded.has(key)) continue
    loaded.add(key)
    const style = document.createElement('style')
    style.textContent = `@font-face{font-family:'${font.name}';src:url('${font.url}')${
      font.format ? ` format('${font.format}')` : ''
    };font-display:swap;}`
    document.head.appendChild(style)
  }
}

export function loadThemeFonts({ fontUrl, fontFamily, headingFontFamily, components, customFonts } = {}) {
  injectCustomFonts(customFonts)
  const customNames = new Set((customFonts || []).map((f) => (f.name || '').toLowerCase()))
  if (fontUrl) {
    loadFontStylesheet(fontUrl)
    return
  }
  const componentFonts = Object.values(components || {}).map((c) => c && c.fontFamily)
  const families = [fontFamily, headingFontFamily, ...componentFonts]
    .filter(Boolean)
    .filter((f) => !customNames.has(f.toLowerCase()))
  families.forEach(loadGoogleFont)
}

// Families a font stylesheet URL provides. Google Fonts links carry them in `family=`
// query params; for self-hosted css the stylesheet is fetched and its font-family
// declarations parsed. Returns [] when nothing can be determined.
export async function parseFontFamilies(url) {
  if (!url) return []
  try {
    const u = new URL(url)
    const fromParams = u.searchParams
      .getAll('family')
      .map((f) => f.split(':')[0].trim())
      .filter(Boolean)
    if (fromParams.length) return [...new Set(fromParams)]
    const css = await (await fetch(url)).text()
    const found = [...css.matchAll(/font-family:\s*['"]([^'"]+)['"]/g)].map((m) => m[1])
    return [...new Set(found)]
  } catch {
    return []
  }
}
